import { useState, useEffect, useRef, useCallback } from 'react'
import { hookLogger } from '../utils/logger'

interface UseContentHeightOptions {
  enabled?: boolean
  includeInput?: boolean  // Whether to include input height in calculation
}

// All height constants live here - single source of truth
const HEIGHT_CONFIG = {
  INITIAL_CHAT_HEIGHT: 250,   // Default when no content
  MAX_CHAT_HEIGHT: 600,        // Maximum chat area height
  INPUT_HEIGHT: 96,            // Input component height
  MESSAGE_BUFFER: 50,          // Extra padding for better UX
  MIN_CONTENT_HEIGHT: 200,     // Minimum even with 1 message
}

export function useContentHeight({
  enabled = true,
  includeInput = true
}: UseContentHeightOptions = {}) {
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null)
  const observerRef = useRef<ResizeObserver | null>(null)
  const elementRef = useRef<HTMLElement | null>(null)

  const measureContent = useCallback(() => {
    if (!elementRef.current || !enabled) return

    const scrollHeight = elementRef.current.scrollHeight
    const contentHeight = Math.min(
      HEIGHT_CONFIG.MAX_CHAT_HEIGHT,
      Math.max(
        HEIGHT_CONFIG.MIN_CONTENT_HEIGHT, 
        scrollHeight + HEIGHT_CONFIG.MESSAGE_BUFFER
      )
    )

    setMeasuredHeight(contentHeight)
    
    hookLogger.debug('Content height measured', {
      scrollHeight,
      contentHeight,
      buffer: HEIGHT_CONFIG.MESSAGE_BUFFER
    })
  }, [enabled])

  const setTargetElement = useCallback((element: HTMLElement | null) => {
    // Disconnect previous observer
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    elementRef.current = element

    if (element && enabled) {
      // Create new observer
      observerRef.current = new ResizeObserver(() => {
        measureContent()
      })
      observerRef.current.observe(element)
      
      // Initial measurement
      measureContent()
    }
  }, [measureContent, enabled])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  // Calculate final height
  const height = (() => {
    if (!enabled) {
      return HEIGHT_CONFIG.INITIAL_CHAT_HEIGHT
    }

    // Use measured height if available, otherwise use initial
    const chatAreaHeight = measuredHeight ?? HEIGHT_CONFIG.INITIAL_CHAT_HEIGHT
    
    // Add input height if requested
    return includeInput 
      ? chatAreaHeight + HEIGHT_CONFIG.INPUT_HEIGHT
      : chatAreaHeight
  })()

  return {
    height,                    // Final computed height
    chatAreaHeight: measuredHeight ?? HEIGHT_CONFIG.INITIAL_CHAT_HEIGHT,  // Just the chat area
    setTargetElement,
    isUsingDefault: measuredHeight === null  // Helpful to know if we're using default
  }
}
